import type { SaveManager } from "@engine/save/SaveManager";
import { STORAGE_KEYS } from "@config/constants/storage";

/**
 * Popphin-specific save schema on top of engine SaveManager.
 * Tracks progress across all Popphin minigames (Ocean Match today, more later).
 */
export interface PopphinSaveState {
  totalSessions: number;
  bestScore: number;
  unlockedCreatures: string[];
  unlockedAchievements: string[];
  lastPlayedISO: string;
}

const EMPTY: PopphinSaveState = {
  totalSessions: 0,
  bestScore: 0,
  unlockedCreatures: [],
  unlockedAchievements: [],
  lastPlayedISO: ""
};

export class PopphinSaveSystem {
  constructor(private readonly save: SaveManager) {}

  async load(): Promise<PopphinSaveState> {
    const state = await this.save.read<PopphinSaveState>(STORAGE_KEYS.SAVE_MAIN);
    if (!state) return { ...EMPTY };
    return {
      totalSessions: typeof state.totalSessions === "number" ? state.totalSessions : 0,
      bestScore: typeof state.bestScore === "number" ? state.bestScore : 0,
      unlockedCreatures: Array.isArray(state.unlockedCreatures) ? state.unlockedCreatures.filter((s) => typeof s === "string") : [],
      unlockedAchievements: Array.isArray(state.unlockedAchievements) ? state.unlockedAchievements.filter((s) => typeof s === "string") : [],
      lastPlayedISO: typeof state.lastPlayedISO === "string" ? state.lastPlayedISO : ""
    };
  }

  async write(state: PopphinSaveState): Promise<void> {
    await this.save.write(STORAGE_KEYS.SAVE_MAIN, state);
  }

  async recordSession(delta: {
    score: number;
    matchedCreatureIds: string[];
    unlockedAchievementIds: string[];
  }): Promise<PopphinSaveState> {
    const state = await this.load();
    state.totalSessions++;
    if (delta.score > state.bestScore) state.bestScore = delta.score;
    for (const id of delta.matchedCreatureIds) {
      if (!state.unlockedCreatures.includes(id)) state.unlockedCreatures.push(id);
    }
    for (const id of delta.unlockedAchievementIds) {
      if (!state.unlockedAchievements.includes(id)) state.unlockedAchievements.push(id);
    }
    state.lastPlayedISO = new Date().toISOString();
    await this.write(state);
    return state;
  }

  async clear(): Promise<void> {
    await this.save.clear(STORAGE_KEYS.SAVE_MAIN);
  }
}
